import { useEffect, useRef } from 'react';
import { subscribeToListingsCountChanges } from '../services/listingsCountSignal';
import { subscribeToCompanyInventoryChanges } from '../services/companyInventoryEvents';

/**
 * How long a focus-driven re-read stands before another focus may ask again.
 * Long enough that alt-tabbing around is free, short enough that a reader who
 * comes back to the tab is not looking at a stale number.
 */
export const LISTINGS_FOCUS_REFRESH_MS = 30_000;

/**
 * Re-read when the set of published listings may have changed.
 *
 * TWO TRIGGERS, ANSWERING DIFFERENT PROBLEMS.
 *
 * The SIGNAL is for writes made in this bundle from a route that cannot reach
 * the listings roster's own change callback: a job feed disconnect ("Its
 * imported roles were unpublished and soft-deleted"), a feed pull, a bulk file
 * import, a facility delete cascading onto its listings. It is never throttled,
 * because it only ever fires after a write has actually landed.
 *
 * THE INVENTORY EVENT is the server reaching in. A scheduled feed pull
 * finishing sends the company a `job_feed_sync` notification, and an admin
 * approving a feed sends `job_feed_review`; NotificationContext turns both into
 * notifyCompanyInventoryChanged in the COMPANY'S OWN BROWSER, which is how the
 * listings and facilities caches already learn to refresh. The count was the
 * one reader on the page not listening, so a feed that had just imported forty
 * roles left the badge on its old number until the next focus. Subscribed
 * rather than re-emitted, so nothing new has to remember to fire it.
 *
 * FOCUS is the backstop for what remains genuinely invisible: a change made in
 * a second tab, or an admin action that sends no notification. It IS throttled:
 * `/listings` is not in api.js's DEDUPE_RULES, so every read is a real network
 * round trip and clicking between two windows must not become one request per
 * click.
 *
 * ONE HOOK FOR BOTH CALLERS ON PURPOSE. CompanyContext owns the topbar badge
 * and CompanyDashboardPage owns the numbers underneath it, and the whole point
 * of this work is that those two stop disagreeing in public. Two copies of this
 * rule would be two chances for them to drift apart again: refreshing the badge
 * on focus while leaving the cards on their mount-time read swaps one visible
 * disagreement for another.
 *
 * @param {() => void} onStale called when a re-read is due. Must be stable.
 * @param {boolean} enabled false unmounts the listeners entirely.
 */
const useListingsFreshness = (onStale, enabled = true) => {
    // Read through a ref so a caller that rebuilds its callback every render
    // does not re-arm the listeners, and the throttle clock survives it.
    const onStaleRef = useRef(onStale);
    onStaleRef.current = onStale;
    // The mount read does not stamp this: that read is the session's first
    // answer, not a re-ask, so the first focus is always allowed to correct it.
    const lastFocusAtRef = useRef(0);

    useEffect(() => {
        if (!enabled) return undefined;

        const fire = () => {
            lastFocusAtRef.current = Date.now();
            if (typeof onStaleRef.current === 'function') onStaleRef.current();
        };
        const onFocus = () => {
            if (Date.now() - lastFocusAtRef.current < LISTINGS_FOCUS_REFRESH_MS) return;
            fire();
        };

        const unsubscribeSignal = subscribeToListingsCountChanges(fire);
        const unsubscribeInventory = subscribeToCompanyInventoryChanges(fire);
        window.addEventListener('focus', onFocus);
        return () => {
            unsubscribeSignal();
            unsubscribeInventory();
            window.removeEventListener('focus', onFocus);
        };
    }, [enabled]);
};

export default useListingsFreshness;
